import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AlertService } from 'app/services/alert.service';
import { LoggerService } from 'app/services/logger.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { ExpenseAdapterService } from '../../services/expense-adapter.service';
import { ExpenseService } from '../../services/expense.service';
import * as ExpenseActions from './expenses.actions';

@Injectable()
export class ExpensesEffects {
    private _actions$ = inject(Actions);
    private logger = inject(LoggerService);
    private _expenseService = inject(ExpenseService);
    private _alertService = inject(AlertService);
    private _adapter = inject(ExpenseAdapterService);

    messages = {
        getAllError: 'Error getting all expenses',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All Expenses
    getAllExpenses$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ExpenseActions.loadExpenses),
            switchMap(() => {
                return this._expenseService.getAll().pipe(
                    map((res) => {
                        return ExpenseActions.loadExpensesSuccess({
                            expenses: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            ExpenseActions.loadExpensesFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Get Show Associated Expenses
    getShowAssociatedExpenses$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ExpenseActions.loadShowExpenses),
            switchMap((action) => {
                return this._expenseService
                    .getShowAssociatedExpenses(action.showId)
                    .pipe(
                        map((res) => {
                            const data = this._adapter.adaptShowExpenses(
                                res.data
                            );
                            return ExpenseActions.loadExpensesSuccess({
                                expenses: data,
                            });
                        }),
                        catchError((error) =>
                            of(
                                ExpenseActions.loadExpensesFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Get Playdate Associated Expenses
    getPlaydateAssociatedExpenses$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ExpenseActions.loadPlaydateExpenses),
            switchMap((action) => {
                return this._expenseService
                    .getPlaydateAssociatedExpenses(action.playdateId)
                    .pipe(
                        map((res) => {
                            const data = this._adapter.adaptPlaydateExpenses(
                                res.data
                            );
                            return ExpenseActions.loadExpensesSuccess({
                                expenses: data,
                            });
                        }),
                        catchError((error) =>
                            of(
                                ExpenseActions.loadExpensesFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Get One Expense
    getExpense$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ExpenseActions.loadExpense),
            switchMap((action) => {
                return this._expenseService.getOne(action.id).pipe(
                    map((res) => {
                        return ExpenseActions.loadExpenseSuccess({
                            expense: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            ExpenseActions.loadExpenseFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Create Expense
    createExpense$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ExpenseActions.createExpense),
            switchMap((action) => {
                return this._expenseService
                    .create(action.data, action.parent)
                    .pipe(
                        map((res) => {
                            return ExpenseActions.createExpenseSuccess({
                                expense: res.data,
                                success: 'Expense created successfully',
                            });
                        }),
                        catchError((error) =>
                            of(
                                ExpenseActions.createExpenseFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Update Expense
    updateExpense$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ExpenseActions.updateExpense),
            switchMap((action) => {
                return this._expenseService
                    .update(action.data, action.id, action.parent)
                    .pipe(
                        map((res) => {
                            return ExpenseActions.updateExpenseSuccess({
                                expense: res.data,
                                success: 'Expense updated successfully',
                            });
                        }),
                        catchError((error) =>
                            of(
                                ExpenseActions.updateExpenseFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Delete Expense
    deleteExpense$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ExpenseActions.deleteExpense),
            switchMap((action) => {
                return this._expenseService.delete(action.id).pipe(
                    map(() => {
                        return ExpenseActions.deleteExpenseSuccess({
                            id: action.id,
                            success: 'Expense deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            ExpenseActions.deleteExpenseFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onExpenseActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    ExpenseActions.createExpenseSuccess,
                    ExpenseActions.updateExpenseSuccess,
                    ExpenseActions.deleteExpenseSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onExpenseActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    ExpenseActions.loadExpensesFailure,
                    ExpenseActions.loadExpenseFailure,
                    ExpenseActions.createExpenseFailure,
                    ExpenseActions.updateExpenseFailure,
                    ExpenseActions.deleteExpenseFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
